import { Container, Graphics, Sprite, Texture, TilingSprite, type Spritesheet } from "pixi.js";

export const MAP_RADIUS = 60;
export const MAP_TILE = 48;

/** Stable decoration only; never used for placement, movement or combat. */
export function landscapeHash(x: number, y: number, salt = 0): number {
  let h = (Math.imul(x, 374761393) + Math.imul(y, 668265263) + Math.imul(salt, 1442695041)) | 0;
  h = Math.imul(h ^ (h >>> 13), 1274126177);
  h ^= h >>> 16;
  return (h >>> 0) / 4294967296;
}

function patch(x: number, y: number): number {
  const gx = Math.floor(x / 7),
    gy = Math.floor(y / 7);
  const smooth = (t: number) => t * t * (3 - 2 * t);
  const fx = smooth(x / 7 - gx),
    fy = smooth(y / 7 - gy);
  const a = landscapeHash(gx, gy, 1),
    b = landscapeHash(gx + 1, gy, 1);
  const c = landscapeHash(gx, gy + 1, 1),
    d = landscapeHash(gx + 1, gy + 1, 1);
  return a + (b - a) * fx + (c + (d - c) * fx - a - (b - a) * fx) * fy;
}

let material: Promise<HTMLImageElement | null> | null = null;
export function loadWorldMaterial(): Promise<HTMLImageElement | null> {
  material ??= new Promise((resolve) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => resolve(null);
    image.src = `${import.meta.env.BASE_URL}art/ancient-meadow.webp`;
  });
  return material;
}

/** A bounded ground texture and cullable groves, shared art but private terrain ownership. */
export class WorldLandscape extends Container {
  private readonly terrain: Texture;
  private readonly materialTexture: Texture | null;
  private readonly groves = new Container();
  private readonly chunks: { view: Container; x: number; y: number }[] = [];

  constructor(
    material: HTMLImageElement | null,
    private readonly art: Spritesheet | null
  ) {
    super();
    this.eventMode = "none";
    // Repeat a crisp small material; only the smooth tint layer spans the world.
    // Both textures together use under 2 MiB, independent of viewport/DPR.
    const canvas = document.createElement("canvas");
    canvas.width = canvas.height = 512;
    const g = canvas.getContext("2d")!;
    const size = (MAP_RADIUS * 2 + 1) * MAP_TILE;
    const k = canvas.width / size;
    g.scale(k, k);
    this.materialTexture = null;
    if (material) {
      const tile = document.createElement("canvas");
      tile.width = tile.height = 384;
      tile.getContext("2d")!.drawImage(material, 0, 0, 384, 384);
      const paint = tile.getContext("2d")!;
      paint.fillStyle = "#80906a55";
      paint.fillRect(0, 0, 384, 384);
      this.materialTexture = Texture.from(tile);
      const grass = new TilingSprite({ texture: this.materialTexture, width: size, height: size });
      grass.position.set(-size / 2, -size / 2);
      this.addChild(grass);
    } else {
      this.addChild(new Graphics().rect(-size / 2, -size / 2, size, size).fill(0x6d7e4e));
    }
    for (let y = -MAP_RADIUS; y <= MAP_RADIUS; y += 3) {
      for (let x = -MAP_RADIUS; x <= MAP_RADIUS; x += 3) {
        const n = patch(x, y);
        const px = (x + MAP_RADIUS + 0.5) * MAP_TILE;
        const py = (MAP_RADIUS - y + 0.5) * MAP_TILE;
        const r = 140 + landscapeHash(x, y, 14) * 100;
        const gradient = g.createRadialGradient(px, py, 0, px, py, r);
        gradient.addColorStop(0, n > 0.58 ? "#294d3570" : n < 0.35 ? "#bdad7245" : "#82964f20");
        gradient.addColorStop(1, "#71804b00");
        g.fillStyle = gradient;
        g.fillRect(px - r, py - r, r * 2, r * 2);
      }
    }
    this.terrain = Texture.from(canvas);
    const picture = new Sprite(this.terrain);
    picture.position.set(-size / 2, -size / 2);
    picture.width = picture.height = size;
    this.addChild(picture, this.groves);
  }

  setVillages(villages: readonly { x: number; y: number }[]): void {
    for (const child of this.groves.removeChildren()) child.destroy({ children: true });
    this.chunks.length = 0;
    if (!this.art) return;
    const taken = new Set<string>();
    for (const v of villages)
      for (let dx = -2; dx <= 2; dx++)
        for (let dy = -2; dy <= 2; dy++)
          if (dx * dx + dy * dy <= 5) taken.add(`${v.x + dx},${v.y + dy}`);
    const groups = new Map<string, Container>();
    const names = [
      "decor_treeHighRound",
      "decor_treeHighRound",
      "decor_treeHigh",
      "decor_treeCrooked",
    ];
    for (let y = MAP_RADIUS; y >= -MAP_RADIUS; y--) {
      for (let x = -MAP_RADIUS; x <= MAP_RADIUS; x++) {
        if (taken.has(`${x},${y}`)) continue;
        const roll = landscapeHash(x, y, 7);
        const region = patch(x, y);
        const forest = region > 0.53;
        const highland = region < 0.25;
        if (roll > (forest ? 0.72 : highland ? 0.24 : 0.02)) continue;
        const name = forest
          ? names[Math.floor(landscapeHash(x, y, 9) * names.length)]!
          : "decor_rockSmall";
        const texture = this.art.textures[name];
        if (!texture) continue;
        const cx = Math.floor(x / 8) * 8 * MAP_TILE,
          cy = Math.floor(-y / 8) * 8 * MAP_TILE;
        const key = `${cx},${cy}`;
        let chunk = groups.get(key);
        if (!chunk) {
          chunk = new Container();
          chunk.position.set(cx, cy);
          this.groves.addChild(chunk);
          groups.set(key, chunk);
          this.chunks.push({ view: chunk, x: cx, y: cy });
        }
        const s = new Sprite(texture);
        s.anchor.set(texture.defaultAnchor?.x ?? 0.5, texture.defaultAnchor?.y ?? 1);
        s.position.set(
          x * MAP_TILE - cx + (landscapeHash(x, y, 11) - 0.5) * 22,
          -y * MAP_TILE - cy + 12
        );
        s.scale.set(forest ? 0.58 + landscapeHash(x, y, 13) * 0.35 : 0.7 + roll * 8);
        chunk.addChild(s);
        if (highland) {
          // Rounded stone outcrops create a recognizable upland, with no
          // drawn impassable edge or invented resource/interaction marker.
          s.scale.set(1.1 + landscapeHash(x, y, 34));
          s.tint = 0xd6d6bb;
          const apron = new Graphics()
            .ellipse(s.x + 4, s.y, 27, 13)
            .fill({ color: 0xb0ac88, alpha: 0.25 });
          chunk.addChildAt(apron, 0);
        }
        // Occasional old standing stones, decoration with no implied route or obstacle.
        if (!forest && landscapeHash(x, y, 19) < 0.1) {
          const clearing = new Graphics()
            .ellipse(s.x, s.y, 26, 14)
            .fill({ color: 0x8d9c77, alpha: 0.2 });
          chunk.addChildAt(clearing, 0);
          for (let i = 0; i < 6; i++) {
            const stone = new Sprite(texture),
              angle = (i * Math.PI) / 3;
            stone.anchor.copyFrom(s.anchor);
            stone.position.set(s.x + Math.cos(angle) * 24, s.y + Math.sin(angle) * 13);
            stone.scale.set(0.7);
            chunk.addChild(stone);
          }
        }
      }
    }
  }

  fit(left: number, top: number, right: number, bottom: number): void {
    for (const chunk of this.chunks) {
      chunk.view.visible =
        chunk.x < right + 80 &&
        chunk.x + 8 * MAP_TILE > left - 80 &&
        chunk.y < bottom + 80 &&
        chunk.y + 8 * MAP_TILE > top - 80;
    }
  }

  override destroy(): void {
    super.destroy({ children: true });
    this.terrain.destroy(true);
    this.materialTexture?.destroy(true);
  }
}
